import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Checkbox from 'components/Checkbox';
import Button from 'components/Button';
import Icon from 'components/Icon';

import css from './DropdownFilter.module.scss';

class DropdownItem extends React.PureComponent {
    static propTypes = {
        id: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        value: PropTypes.string,
        onClick: PropTypes.func,
        tabbable: PropTypes.bool,
        active: PropTypes.bool,
        checkable: PropTypes.bool,
        group: PropTypes.bool,
        zebra: PropTypes.bool
    }

    static defaultProps = {
        tabbable: false,
        active: false,
        group: false,
        zebra: false
    }

    render() {
        let { id, value, active, group, zebra, tabbable, checkable, onClick } = this.props;
        return (
            <li
                data-id={id}
                data-state={active ? 1 : 0}
                onClick={onClick}
                onKeyDown={(e) => {
                    let code = e.which;
                    // 13 = Return, 32 = Space
                    if ((code === 13) || (code === 32)) {
                        e.stopPropagation();
                        e.preventDefault();
                        onClick(e);
                    }
                }}
                tabIndex={tabbable ? 0 : -1}
                className={cn({
                    [css['dropdown-group']]: group,
                    [css['dropdown-item']]: !group,
                    [css['dropdown-check']]: checkable,
                    [css['active']]: active,
                    [css['zebra']]: zebra
                })}
            >
                {checkable
                    ? <Checkbox checked={active} tabbable={false}>
                        {value}
                    </Checkbox>
                    : value
                }
            </li>
        );
    }
}

class DropdownFilter extends React.Component {
    static propTypes = {
        heading: PropTypes.string.isRequired,
        prefix: PropTypes.string,
        items: PropTypes.array.isRequired,
        onSelect: PropTypes.func,
        multi: PropTypes.bool,
        multiLabel: PropTypes.string,
        className: PropTypes.string,
        active: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.array
        ]),
        yellow: PropTypes.bool,
        name: PropTypes.string
    }

    constructor(props) {
        super();

        this.state = {
            isOpen: false,
            active: this.parseIncomingActiveProp(props)
        };
    }

    componentWillUpdate = (props) => {
        // set new active from props
        if (this.props.active !== props.active) {
            this.setState({ active: this.parseIncomingActiveProp(props) });
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        let active = this.state.active;
        // add click outside listener
        if (prevState.isOpen !== this.state.isOpen) {
            if (this.state.isOpen === true) {
                document && document.addEventListener("click", this._handleOutsideclick);
                // set focus to first active element
                if (active.length)
                    this.node.querySelector("li[data-id='" + active[0] + "']").focus();
            } else {
                document && document.removeEventListener("click", this._handleOutsideclick);
            }
        }
    }

    componentWillUnmount = () => {
        document && document.removeEventListener("click", this._handleOutsideclick);
    }

    parseIncomingActiveProp = (props) => {
        let newActive = props.active;
        if (props.multi) {
            newActive = props.active ? props.active.map(obj => {
                if (typeof obj === "object") {
                    return obj.key.toString();
                }
                return obj.toString();
            }) : [];

            // override if all exists
            if (newActive.indexOf("") >= 0) {
                newActive = props.items.map(i => i.key.toString());
            }
        } else {
            if (typeof props.active === 'string' || typeof props.active === 'number') {
                newActive = [props.active];
            } else if (props.items.length) {
                newActive = [props.items[0].key];
            } else {
                newActive = [""];
            }
        }
        return newActive;
    }

    getActiveItems = () => {
        const { items } = this.props;
        const { active } = this.state;
        let values = {};
        items.forEach(i => {
            if (i.items) {
                i.items.forEach(sub => {
                    if (active.filter(a => sub.key.toString() === a.toString()).length) {
                        values[sub.key.toString()] = sub;
                    }
                });
            } else {
                if (active.filter(a => i.key.toString() === a.toString()).length) {
                    values[i.key.toString()] = i;
                }
            }
        });

        return values;
    }

    toggle = () => {
        this.setState({ isOpen: !this.state.isOpen });
    }
    open = () => {
        if (this.state.isOpen) {
            return;
        }
        this.setState({ isOpen: true });
    }
    close = () => {
        if (!this.state.isOpen) {
            return;
        }
        this.setState({ isOpen: false });
        this.node.focus();
    }

    _handleOutsideclick = e => {
        if (e && this.node.contains(e.target)) {
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        this.close();
    }

    _handleItemClick = e => {
        e.preventDefault();
        let { active } = this.state;
        const { items, multi, name } = this.props;
        const { id: currentId, state: currentActive } = e.currentTarget.dataset;

        const exists = active.indexOf(currentId.toString());

        // 'All' toggle checkbox
        if (currentId === "" || currentId == 0) {
            if (currentActive == "0") {
                active = items.map(i => i.key && i.key.toString());
            } else {
                active = [];
            }
        }

        // autoclose if we click the same element again - unless this is a multi selector
        if (multi) {
            // add or remove item from active list
            if (currentId !== "") {
                if (exists > -1) {
                    // remove
                    active.splice(exists, 1);
                } else {
                    // add
                    active.push(currentId.toString());
                }
            }

            // handle "all" marking
            const allPos = active.indexOf("");
            if (allPos > -1 && active.length < items.length) {
                active.splice(allPos, 1);
            } else if (allPos === -1 && active.length === items.length - 1) {
                active.push("");
            }
        } else {
            if (exists > -1) {
                this.close();
                return;
            }
            active = [currentId.toString()];
        }

        this.setState({ active: active });

        // don't autoclose when multi selector
        if (!multi) {
            this.close();
        }
        // trigger onSelect function
        this.props.onSelect && this.props.onSelect(e, multi ? active : active[0], name); // multi - entire array. single - only return the id
    }

    _handleBlur = e => {
        e.preventDefault();
        this.close();
    }

    render() {
        const {
            heading,
            prefix,
            items,
            multi,
            multiLabel,
            onSelect,
            className,
            yellow,
        } = this.props; // get active (as activeProp) to exclude it from ...rest
        const { isOpen, active } = this.state;

        const activeItems = this.getActiveItems();

        let displayHeading = multi
            ? <div><span className={css.number}>{active.length}</span> {multiLabel}</div>
            : (!active.length ? "-" : (activeItems[active[0]] && activeItems[active[0]].value !== undefined ? activeItems[active[0]].value : "-"));

        const item = (o, i) => {
            // if this is an item group
            if (o.items) {
                return [
                    <DropdownItem
                        {...o}
                        group={true}
                        key={"group_" + o.key + "_" + i}
                    />,
                    ...o.items.map(item)
                ];
            }
            return <DropdownItem
                {...o}
                tabbable={true}
                key={o.key + "_" + i}
                id={o.key}
                active={typeof activeItems[o.key] !== "undefined"}
                checkable={multi}
                onClick={this._handleItemClick}
                zebra={i % 2 === 0}
            />;
        };

        const list = items.map(item);
        return (
            <div
                tabIndex="0"
                className={cn(css.block, className, {
                    [css['open']]: isOpen,
                    [css['single']]: !multi,
                    [css['multi']]: multi,
                    [css.yellow]: yellow,
                })}
                ref={t => this.node = t}
                onClick={this.open}
                onKeyDown={(e) => {
                    let code = e.which;
                    // 27 = Esc, 13 = Return, 32 = Space
                    if ((code === 13) || (code === 32)) {
                        e.preventDefault();
                        this.toggle();
                    }
                    if (code === 27 && this.state.isOpen) {
                        e.preventDefault();
                        this.close();
                    }
                }}
                onSelect={onSelect}
            >
                <h4>{heading}</h4>
                {isOpen && <div onClick={this.close} className={css.overlay} />}
                <div className={css.dropdown}>
                    <span>
                        <span>{prefix && <span>{prefix}</span>}{displayHeading}</span>
                        <Icon icon="arrow-down" className={cn(css.arrow, {
                            [css['open']]: isOpen
                        })} />
                    </span>
                    <div>
                        <ul className={cn({
                            [css['open']]: isOpen
                        })}>
                            {list}
                        </ul>
                        <Button icon="arrow-down" className={css.close} onClick={this.close}></Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default DropdownFilter;



// WEBPACK FOOTER //
// ./src/components/DropdownFilter/DropdownFilter.jsx